import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, ClipboardList, PoundSterling, Star, Store, Settings,
  Plus, Pencil, Trash2, Loader2, ArrowLeft, TrendingUp, ShoppingBag, Clock, CheckCircle2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api, { imageUrl, money, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoWord } from "@/components/Logo";
import { ImageUpload } from "@/components/ImageUpload";
import { StatusBadge, STATUS_META, ALL_STATUSES } from "@/components/OrderStatus";
import { Stars } from "@/components/Stars";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NAV = [
  { key: "", label: "Dashboard", icon: LayoutDashboard },
  { key: "products", label: "Products", icon: Package },
  { key: "orders", label: "Orders", icon: ClipboardList },
  { key: "sales", label: "Sales", icon: PoundSterling },
  { key: "reviews", label: "Reviews", icon: Star },
  { key: "profile", label: "Profile", icon: Store },
  { key: "settings", label: "Settings", icon: Settings },
];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white p-5 rounded-2xl border border-[#E5DEC9]">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent || "bg-[#1A3C2A]/8 text-[#1A3C2A]"}`}><Icon size={20} /></div>
      <p className="text-2xl font-display font-extrabold text-[#1A3C2A] mt-3">{value}</p>
      <p className="text-xs text-[#5A626A] font-semibold">{label}</p>
    </div>
  );
}

export default function SellerDashboard() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tab = pathname.replace(/^\/seller\/?/, "").split("/")[0];

  const [profile, setProfile] = useState(null);
  const [sales, setSales] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ready, setReady] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [p, s, pr, o, c] = await Promise.all([
        api.get("/seller/profile/me").catch(() => ({ data: null })),
        api.get("/seller/sales").catch(() => ({ data: null })),
        api.get("/seller/products").catch(() => ({ data: [] })),
        api.get("/seller/orders").catch(() => ({ data: [] })),
        api.get("/categories"),
      ]);
      setProfile(p.data); setSales(s.data); setProducts(pr.data); setOrders(o.data); setCategories(c.data);
    } catch {} finally { setReady(true); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  if (!ready) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="animate-spin text-[#1A3C2A]" />
        <p className="text-sm text-[#5A626A]">Loading your shop…</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="seller-no-shop">
        <div className="w-16 h-16 rounded-2xl bg-[#1A3C2A]/8 flex items-center justify-center"><Store size={30} className="text-[#1A3C2A]" /></div>
        <div>
          <h1 className="font-display text-2xl font-bold text-[#1A3C2A]">You don't have a shop yet</h1>
          <p className="text-sm text-[#5A626A] mt-1 max-w-sm">Set up your Nabor storefront to start listing products and receiving orders.</p>
        </div>
        <Button data-testid="setup-shop-button" onClick={() => navigate("/sell?setup=1")} className="rounded-full bg-[#1A3C2A] hover:bg-[#132E20] h-11 px-6 font-semibold gap-2">
          <Store size={17} /> Set up your shop
        </Button>
        <Link to="/" className="text-sm font-semibold text-[#E07A5F]">Back to marketplace</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 p-4 sm:p-6 lg:p-8">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          <div className="bg-white border border-[#E5DEC9] rounded-2xl p-4 lg:sticky lg:top-6">
            <Link to="/" className="flex items-center gap-2 text-xs text-[#5A626A] mb-4 hover:text-[#1A3C2A]"><ArrowLeft size={14} /> Back to marketplace</Link>
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#E5DEC9]">
              <img src={imageUrl(profile?.logo)} alt="" className="w-11 h-11 rounded-xl object-cover bg-[#F4EFE6]" />
              <div className="min-w-0"><p className="font-display font-bold text-[#1A3C2A] truncate">{profile?.shop_name}</p><p className="text-xs text-[#5A626A]">Seller</p></div>
            </div>
            <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
              {NAV.map((n) => {
                const active = tab === n.key;
                const Icon = n.icon;
                return (
                  <Link key={n.key} to={`/seller${n.key ? "/" + n.key : ""}`} data-testid={`seller-tab-${n.key || "dashboard"}`}
                    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${active ? "bg-[#1A3C2A] text-white" : "text-[#22252A] hover:bg-[#1A3C2A]/5"}`}>
                    <Icon size={17} /> {n.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main */}
        <main className="lg:col-span-9 space-y-6 pb-20 lg:pb-0">
          {tab === "" && <Overview sales={sales} orders={orders} />}
          {tab === "products" && <Products products={products} categories={categories} reload={loadAll} />}
          {tab === "orders" && <Orders orders={orders} reload={loadAll} />}
          {tab === "sales" && <Sales sales={sales} />}
          {tab === "reviews" && <Reviews profile={profile} />}
          {tab === "profile" && <Profile profile={profile} categories={categories} reload={loadAll} />}
          {tab === "settings" && <SettingsPanel profile={profile} />}
        </main>
      </div>
    </div>
  );
}

function Overview({ sales, orders }) {
  const recent = orders.slice(0, 5);
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Stat icon={PoundSterling} label="Total sales" value={money(sales?.total_sales || 0)} accent="bg-[#E07A5F]/10 text-[#E07A5F]" />
        <Stat icon={ShoppingBag} label="Orders" value={sales?.order_count || 0} />
        <Stat icon={Clock} label="Pending" value={sales?.pending_count || 0} accent="bg-amber-100 text-amber-600" />
        <Stat icon={CheckCircle2} label="Completed" value={sales?.completed_count || 0} accent="bg-green-100 text-green-600" />
      </div>
      <div className="bg-white border border-[#E5DEC9] rounded-2xl p-5">
        <h2 className="font-display font-bold text-[#1A3C2A] mb-4 flex items-center gap-2"><TrendingUp size={18} /> Sales over time</h2>
        {sales?.chart?.length ? <SalesChart data={sales.chart} /> : <p className="text-sm text-[#5A626A] py-8 text-center">No sales yet — share your shop to get your first order!</p>}
      </div>
      <div className="bg-white border border-[#E5DEC9] rounded-2xl p-5">
        <h2 className="font-display font-bold text-[#1A3C2A] mb-3">Recent orders</h2>
        {recent.length === 0 ? <p className="text-sm text-[#5A626A]">No orders yet.</p> : (
          <div className="space-y-2">
            {recent.map((o) => (
              <div key={o.id} className="flex items-center justify-between text-sm py-2 border-b border-[#E5DEC9] last:border-0">
                <span className="font-semibold text-[#22252A]">{o.order_number}</span>
                <span className="text-[#5A626A]">{o.customer_name}</span>
                <span className="font-display font-bold text-[#1A3C2A]">{money(o.total)}</span>
                <StatusBadge status={o.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function SalesChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5DEC9" vertical={false} />
        <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} fontSize={11} stroke="#5A626A" />
        <YAxis fontSize={11} stroke="#5A626A" tickFormatter={(v) => "£" + v} />
        <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E5DEC9" }} />
        <Bar dataKey="sales" fill="#1A3C2A" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

const emptyProduct = { name: "", description: "", price: "", category: "", stock: 0, available: true, images: [] };

function Products({ products, categories, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(emptyProduct); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, price: String(p.price) }); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Name and price are required");
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) || 0 };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const toggleAvail = async (p) => {
    try { await api.put(`/products/${p.id}`, { ...p, available: !p.available }); reload(); } catch { toast.error("Failed"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); reload(); } catch { toast.error("Failed"); }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Products</h1>
        <Button data-testid="add-product-button" onClick={openNew} className="rounded-full bg-[#1A3C2A] hover:bg-[#132E20] gap-1"><Plus size={16} /> Add product</Button>
      </div>
      {products.length === 0 ? (
        <div className="bg-white border border-[#E5DEC9] rounded-2xl p-10 text-center">
          <Package size={36} className="text-[#1A3C2A] mx-auto" />
          <p className="font-display font-bold text-[#1A3C2A] mt-2">No products yet</p>
          <p className="text-sm text-[#5A626A]">Add your first product to start selling.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {products.map((p) => (
            <div key={p.id} data-testid={`seller-product-${p.id}`} className="bg-white border border-[#E5DEC9] rounded-2xl p-3 flex gap-3">
              <img src={imageUrl((p.images || [])[0])} alt="" className="w-20 h-20 rounded-xl object-cover bg-[#F4EFE6] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-2">
                  <h3 className="font-display font-semibold text-[#22252A] truncate">{p.name}</h3>
                  <span className="font-display font-bold text-[#1A3C2A]">{money(p.price)}</span>
                </div>
                <p className="text-xs text-[#5A626A]">{p.category} · {p.stock} in stock</p>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <Switch data-testid={`toggle-avail-${p.id}`} checked={p.available} onCheckedChange={() => toggleAvail(p)} />
                    <span className="text-xs text-[#5A626A]">{p.available ? "Available" : "Hidden"}</span>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-[#1A3C2A]/5 text-[#1A3C2A]"><Pencil size={15} /></button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => del(p)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs mb-1 block">Product image</Label><ImageUpload testid="product-image" value={form.images?.[0] || ""} onChange={(v) => setForm({ ...form, images: v ? [v] : [] })} label="Photo" /></div>
            <div><Label className="text-xs">Name</Label><Input data-testid="product-name" value={form.name} onChange={set("name")} className="rounded-xl mt-1" /></div>
            <div><Label className="text-xs">Description</Label><Textarea data-testid="product-description" value={form.description} onChange={set("description")} className="rounded-xl mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Price (£)</Label><Input data-testid="product-price" type="number" step="0.01" value={form.price} onChange={set("price")} className="rounded-xl mt-1" /></div>
              <div><Label className="text-xs">Stock</Label><Input data-testid="product-stock" type="number" value={form.stock} onChange={set("stock")} className="rounded-xl mt-1" /></div>
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="product-category" className="rounded-xl mt-1"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} /><span className="text-sm text-[#5A626A]">Available for sale</span></div>
            <Button data-testid="save-product-button" onClick={save} disabled={saving} className="w-full rounded-full bg-[#1A3C2A] hover:bg-[#132E20]">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save product"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Orders({ orders, reload }) {
  const setStatus = async (o, status) => {
    try { await api.put(`/orders/${o.id}/status`, { status }); toast.success(`Order ${status.replace("_", " ")}`); reload(); }
    catch { toast.error("Could not update"); }
  };
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Orders</h1>
      {orders.length === 0 ? <div className="bg-white border border-[#E5DEC9] rounded-2xl p-10 text-center text-[#5A626A]">No orders yet.</div> : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`seller-order-${o.id}`} className="bg-white border border-[#E5DEC9] rounded-2xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div><p className="font-display font-bold text-[#1A3C2A]">{o.order_number}</p><p className="text-xs text-[#5A626A]">{o.customer_name} · {o.fulfilment} · {new Date(o.created_at).toLocaleDateString("en-GB")}</p></div>
                <div className="flex items-center gap-2">{o.payment_status !== "paid" && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-red-50 text-red-600">Unpaid</span>}<StatusBadge status={o.status} testid={`seller-order-status-${o.id}`} /></div>
              </div>
              <div className="text-sm text-[#5A626A] mb-3">{o.items.map((li) => `${li.quantity}× ${li.name}`).join(", ")} · <span className="font-bold text-[#1A3C2A]">{money(o.total)}</span></div>
              {o.fulfilment === "delivery" && o.address_line1 && <p className="text-xs text-[#5A626A] bg-[#FAF7F2] rounded-lg p-2 mb-3">📍 {o.address_line1}, {o.city} {o.postcode}</p>}
              {o.status === "pending" ? (
                <div className="flex gap-2">
                  <Button data-testid={`accept-order-${o.id}`} onClick={() => setStatus(o, "accepted")} className="rounded-full bg-[#1A3C2A] hover:bg-[#132E20] h-9">Accept</Button>
                  <Button data-testid={`reject-order-${o.id}`} onClick={() => setStatus(o, "cancelled")} variant="outline" className="rounded-full border-red-200 text-red-600 h-9">Reject</Button>
                </div>
              ) : o.status !== "cancelled" && o.status !== "completed" ? (
                <Select value={o.status} onValueChange={(v) => setStatus(o, v)}>
                  <SelectTrigger data-testid={`order-status-select-${o.id}`} className="w-56 rounded-full"><SelectValue /></SelectTrigger>
                  <SelectContent>{ALL_STATUSES.filter((s) => s !== "pending").map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
                </Select>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Sales({ sales }) {
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Sales & earnings</h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat icon={PoundSterling} label="Total earnings" value={money(sales?.total_sales || 0)} accent="bg-[#E07A5F]/10 text-[#E07A5F]" />
        <Stat icon={ShoppingBag} label="Paid orders" value={sales?.paid_count || 0} />
        <Stat icon={Package} label="Products" value={sales?.product_count || 0} />
      </div>
      <div className="bg-white border border-[#E5DEC9] rounded-2xl p-5">
        <h2 className="font-display font-bold text-[#1A3C2A] mb-4">Daily sales</h2>
        {sales?.chart?.length ? <SalesChart data={sales.chart} /> : <p className="text-sm text-[#5A626A] py-8 text-center">No paid sales yet.</p>}
      </div>
    </>
  );
}

function Reviews({ profile }) {
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Reviews</h1>
      <div className="bg-white border border-[#E5DEC9] rounded-2xl p-5 flex items-center gap-4">
        <div className="text-center"><p className="font-display text-4xl font-extrabold text-[#1A3C2A]">{profile?.rating || 0}</p><Stars rating={profile?.rating} showNumber={false} /></div>
        <p className="text-sm text-[#5A626A]">Based on {profile?.review_count || 0} customer review{profile?.review_count !== 1 ? "s" : ""}.</p>
      </div>
      <div className="space-y-3">
        {(!profile?.reviews || profile.reviews.length === 0) ? <p className="text-sm text-[#5A626A]">No reviews yet.</p> : profile.reviews.map((r) => (
          <div key={r.id} className="bg-white border border-[#E5DEC9] rounded-2xl p-4">
            <div className="flex items-center justify-between"><span className="font-semibold text-[#22252A]">{r.user_name}</span><Stars rating={r.rating} showNumber={false} /></div>
            {r.comment && <p className="text-sm text-[#5A626A] mt-1">{r.comment}</p>}
          </div>
        ))}
      </div>
    </>
  );
}

function Profile({ profile, categories, reload }) {
  const [form, setForm] = useState({ ...profile });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try { await api.post("/seller/profile", form); toast.success("Shop updated"); reload(); }
    catch { toast.error("Failed"); } finally { setSaving(false); }
  };
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Shop profile</h1>
      <div className="bg-white border border-[#E5DEC9] rounded-2xl p-5 space-y-4">
        <div className="flex gap-4">
          <div><Label className="text-xs mb-1 block">Logo</Label><ImageUpload testid="profile-logo" value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} label="Logo" /></div>
          <div className="flex-1"><Label className="text-xs mb-1 block">Cover</Label><ImageUpload testid="profile-cover" value={form.cover} onChange={(v) => setForm({ ...form, cover: v })} label="Cover" /></div>
        </div>
        <div><Label className="text-xs">Shop name</Label><Input data-testid="profile-shop-name" value={form.shop_name || ""} onChange={set("shop_name")} className="rounded-xl mt-1" /></div>
        <div><Label className="text-xs">Description</Label><Textarea data-testid="profile-desc" value={form.description || ""} onChange={set("description")} className="rounded-xl mt-1" /></div>
        <div>
          <Label className="text-xs">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Area</Label><Input data-testid="profile-area" value={form.area || ""} onChange={set("area")} className="rounded-xl mt-1" /></div>
          <div><Label className="text-xs">Postcode</Label><Input data-testid="profile-postcode" value={form.postcode || ""} onChange={set("postcode")} className="rounded-xl mt-1" /></div>
        </div>
        <Button data-testid="save-profile-button" onClick={save} disabled={saving} className="rounded-full bg-[#1A3C2A] hover:bg-[#132E20]">{saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </>
  );
}

function SettingsPanel({ profile }) {
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Settings</h1>
      <div className="bg-white border border-[#E5DEC9] rounded-2xl p-5 space-y-3">
        <p className="text-sm text-[#5A626A]">Shop status: <span className="font-semibold text-[#1A3C2A] capitalize">{profile?.status}</span></p>
        <p className="text-sm text-[#5A626A]">Delivery fee: <span className="font-semibold text-[#1A3C2A]">£2.99 per order</span></p>
        <p className="text-xs text-[#9A9282]">Payouts, custom delivery zones and notifications are coming soon.</p>
      </div>
    </>
  );
}
